import type { MarketComponentMarketComponentIO } from '../../generated/fresh-guard-api';

const getRotatedCorners = (component: MarketComponentMarketComponentIO) => {
  const toRadians = (angle: number) => (angle * Math.PI) / 180;
  const x = component.position?.x ?? 0;
  const y = component.position?.y ?? 0;
  const width = component.size?.width ?? 0;
  const height = component.size?.height ?? 0;
  const angle = toRadians(component.orientation ?? 0);
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  const cx = x + halfWidth;
  const cy = y + halfHeight;

  return [
    { x: -halfWidth, y: -halfHeight },
    { x: halfWidth, y: -halfHeight },
    { x: halfWidth, y: halfHeight },
    { x: -halfWidth, y: halfHeight },
  ].map(({ x, y }) => ({
    x: parseFloat((cx + x * Math.cos(angle) - y * Math.sin(angle)).toFixed(5)),
    y: parseFloat((cy + x * Math.sin(angle) + y * Math.cos(angle)).toFixed(5)),
  }));
};

const isColliding = (
  rectA: MarketComponentMarketComponentIO,
  rectB: MarketComponentMarketComponentIO
) => {
  const EPSILON = 1e-12;
  const cornersA = getRotatedCorners(rectA);
  const cornersB = getRotatedCorners(rectB);

  const getAxes = (corners: { x: number; y: number }[]) => {
    return corners.map((p1, i) => {
      const p2 = corners[(i + 1) % corners.length];
      return { x: -(p2.y - p1.y), y: p2.x - p1.x };
    });
  };

  const project = (corners: { x: number; y: number }[], axis: { x: number; y: number }) => {
    const dots = corners.map((corner) => corner.x * axis.x + corner.y * axis.y);
    return { min: Math.min(...dots), max: Math.max(...dots) };
  };

  const axes = [...getAxes(cornersA), ...getAxes(cornersB)];

  let touching = false;

  for (const axis of axes) {
    const projectionA = project(cornersA, axis);
    const projectionB = project(cornersB, axis);

    if (
      projectionA.max < projectionB.min + EPSILON ||
      projectionB.max < projectionA.min + EPSILON
    ) {
      return false;
    }

    if (
      Math.abs(projectionA.max - projectionB.min) < EPSILON ||
      Math.abs(projectionB.max - projectionA.min) < EPSILON
    ) {
      touching = true;
    }
  }

  return !touching;
};

const hasCollision = (
  marketComponent: MarketComponentMarketComponentIO,
  marketComponents: MarketComponentMarketComponentIO[]
) => {
  return marketComponents.some(
    (item) => item.id !== marketComponent.id && isColliding(marketComponent, item)
  );
};

export { hasCollision };
